import React, { useState, useEffect } from 'react';

/**
 * ResponsiveTable Component
 * Displays a table on desktop and cards on mobile
 */
export const ResponsiveTable = ({ children, data, renderMobileCard, keyPrefix = "", getItemKey }) => {
  console.log('ResponsiveTable rendering with data:', data?.length, 'items');
  return (
    <>
      {/* Desktop view - shows regular table */}
      <div className="hidden md:block overflow-x-auto">
        {children}
      </div>

      {/* Mobile view - shows cards instead */}
      <div className="md:hidden space-y-4">
        {data && data.length > 0 ? (
          data.map((item, index) => {
            // Use custom key generator if provided, otherwise default to item.id or index
            let key;
            if (getItemKey) {
              key = getItemKey(item);
            } else {
              key = keyPrefix ? `${keyPrefix}-${item.id || index}` : (item.id || index);
            }
            return (
              <div key={key}>
                {renderMobileCard(item)}
              </div>
            );
          })
        ) : (
          <div className="p-4 text-center text-gray-500">
            <p>No data available</p>
          </div>
        )}
      </div>
    </>
  );
};

/**
 * Mobile Sidebar Component
 * Slide-out navigation for mobile devices
 */
export const MobileSidebar = ({ activeTab, onTabChange, isOpen, onToggle, navigate }) => {
  const [activeMenu, setActiveMenu] = useState('main');
  
  const navItems = [
    { id: 'home', label: 'Home', icon: '🏠' },
    { id: 'students', label: 'Students', icon: '👥' },
    { id: 'recruiters', label: 'Recruiters', icon: '💼' },
    { id: 'university-partners', label: 'University Partners', icon: '🎓' },
    { id: 'companies', label: 'All Companies', icon: '🏢' },
    { id: 'profile', label: 'Logout', icon: '🚪' },
  ];

  const studentMenuItems = [
    { 
      id: 'student-activity', 
      label: 'Student Activity', 
      route: '/admin?tab=student-activity',
      icon: '📊',
      description: 'View student activity logs'
    },
    { 
      id: 'student-matches', 
      label: 'Student Matches', 
      route: '/admin/student-matches',
      icon: '🎯',
      description: 'Manage organization matches'
    },
    { 
      id: 'non-matching', 
      label: 'Non-matching Students', 
      route: '/admin/non-matching-students',
      icon: '❓',
      description: 'Students without matches'
    },
    { 
      id: 'approve-students', 
      label: 'Approve Students', 
      route: '/admin?tab=approve-students',
      icon: '✅',
      description: 'Review and approve students'
    },
    { 
      id: 'tokens', 
      label: 'Student Tokens', 
      route: '/admin?tab=tokens',
      icon: '🔑',
      description: 'Manage student tokens'
    }
  ];

  const recruiterMenuItems = [
    { 
      id: 'recruiter-activity', 
      label: 'Recruiter Activity', 
      route: '/admin?tab=recruiter-activity',
      icon: '📈',
      description: 'View recruiter activity logs'
    },
    { 
      id: 'organization-matches', 
      label: 'Organization Matches', 
      route: '/admin/organization-matches',
      icon: '🏢',
      description: 'Manage organization matches'
    },
    { 
      id: 'multi-company', 
      label: 'Multi-Company Students', 
      route: '/admin/multi-company-students',
      icon: '👥',
      description: 'Students with multiple offers'
    },
    { 
      id: 'company-management', 
      label: 'Company Management', 
      route: '/admin/company-management',
      icon: '⚙️',
      description: 'Activate/deactivate companies'
    },
    { 
      id: 'recruiter-tokens', 
      label: 'Recruiter Tokens', 
      route: '/admin/recruiter-tokens',
      icon: '🎫',
      description: 'Manage recruiter tokens'
    }
  ];

  const universityMenuItems = [
    { 
      id: 'partner-activity', 
      label: 'Partner Activity', 
      route: '/admin?tab=partner-activity',
      icon: '📊',
      description: 'View partner activity logs'
    },
    { 
      id: 'partner-accounts', 
      label: 'Partner Accounts', 
      route: '/admin?tab=partner-accounts',
      icon: '👤',
      description: 'Manage partner accounts'
    },
    { 
      id: 'dashboard', 
      label: 'Dashboard', 
      route: '/admin?tab=dashboard',
      icon: '📈',
      description: 'Partner dashboard overview'
    },
    { 
      id: 'partner-tokens', 
      label: 'Partner Tokens', 
      route: '/admin?tab=partner-tokens',
      icon: '🔑',
      description: 'Manage partner tokens'
    }
  ];

  // Scalable menu configuration - easy to extend with future menus
  const menuConfig = {
    students: { 
      label: 'Student Management', 
      items: studentMenuItems 
    },
    recruiters: { 
      label: 'Recruiter Management', 
      items: recruiterMenuItems 
    },
    'university-partners': { 
      label: 'University Partners', 
      items: universityMenuItems 
    }
    // Future menus can be easily added here:
    // analytics: { label: 'Analytics', items: analyticsMenuItems },
    // reports: { label: 'Reports', items: reportsMenuItems },
  };

  // Scalable menu rendering function
  const renderMenuContent = () => {
    if (activeMenu === 'main') {
      // Main navigation
      return navItems.map(item => (
        <button
          key={item.id}
          onClick={() => {
            if (menuConfig[item.id]) {
              setActiveMenu(item.id);
            } else if (item.id === 'companies') {
              navigate && navigate('/companies');
              onToggle(); // Close sidebar after navigation
            } else if (item.id === 'profile') {
              // Logout functionality
              localStorage.removeItem('token');
              localStorage.removeItem('authToken');
              navigate && navigate('/admin/login');
              onToggle(); // Close sidebar after navigation
            } else {
              onTabChange && onTabChange(item.id);
              onToggle(); // Close sidebar after navigation
            }
          }}
          className={`w-full flex items-center px-6 py-3 text-left transition-colors ${
            activeTab === item.id 
              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-2 border-blue-600 dark:border-blue-400' 
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="text-xl mr-3">{item.icon}</span>
          <span className="font-medium">{item.label}</span>
        </button>
      ));
    } else {
      // Submenu (Students, Recruiters, University Partners, etc.)
      const currentMenu = menuConfig[activeMenu];
      if (!currentMenu) return null;

      return (
        <div className="px-4">
          <button
            onClick={() => setActiveMenu('main')}
            className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mb-4"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Main Menu
          </button>
          
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {currentMenu.label}
          </h3>
          
          <div className="grid grid-cols-2 gap-3">
            {currentMenu.items.map(item => (
              <button
                key={item.id}
                onClick={() => {
                  if (navigate) {
                    navigate(item.route);
                  }
                  setActiveMenu('main');
                  onToggle(); // Close sidebar after navigation
                }}
                className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors text-center"
              >
                <span className="text-2xl mb-2">{item.icon}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {item.label}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {item.description}
                </span>
              </button>
            ))}
          </div>
        </div>
      );
    }
  };

  return (
    <>
      {/* Floating Hamburger Menu Button */}
      <button
        onClick={onToggle}
        className="md:hidden fixed top-4 left-4 z-40 p-2 bg-white dark:bg-gray-800 rounded-md shadow-lg text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="Toggle navigation menu"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Overlay */}
      {isOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div className={`md:hidden fixed top-0 left-0 h-full w-64 bg-white dark:bg-gray-800 transform transition-transform duration-300 ease-in-out z-50 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Navigation</h2>
          <button
            onClick={onToggle}
            className="p-1 rounded-md text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close navigation menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <nav className="mt-4">
          {renderMenuContent()}
        </nav>
      </div>
    </>
  );
};

/**
 * Mobile Navigation Component
 * Fixed bottom navigation for mobile devices
 */
export const MobileNav = ({ activeTab, onTabChange }) => {
  const navItems = [
    { id: 'home', label: 'Home', icon: '🏠' },
    { id: 'students', label: 'Students', icon: '👥' },
    { id: 'recruiters', label: 'Recruiters', icon: '💼' },
    { id: 'profile', label: 'Profile', icon: '👤' },
  ];

  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-50">
      <nav className="flex justify-around py-2">
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => onTabChange && onTabChange(item.id)}
            className={`flex flex-col items-center p-2 min-w-[64px] ${
              activeTab === item.id 
                ? 'text-blue-600 dark:text-blue-400' 
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            <span className="text-xl mb-1">{item.icon}</span>
            <span className="text-xs">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

/**
 * Mobile Card Component for Recruiters
 */
export const MobileRecruiterCard = ({ recruiter, onApprove, onReject, onView }) => {
  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
            {recruiter.name}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {recruiter.organization}
          </p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          recruiter.approved 
            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
        }`}>
          {recruiter.approved ? 'Approved' : 'Pending'}
        </span>
      </div>
      
      <div className="space-y-2 text-sm mb-4">
        <div>
          <span className="font-medium text-gray-700 dark:text-gray-300">Email:</span>
          <p className="text-gray-600 dark:text-gray-400 break-all">{recruiter.email}</p>
        </div>
        <div>
          <span className="font-medium text-gray-700 dark:text-gray-300">Title:</span>
          <p className="text-gray-600 dark:text-gray-400">{recruiter.title || 'N/A'}</p>
        </div>
        <div>
          <span className="font-medium text-gray-700 dark:text-gray-300">Applied:</span>
          <p className="text-gray-600 dark:text-gray-400">
            {new Date(recruiter.created_at).toLocaleDateString()}
          </p>
        </div>
      </div>
      
      <div className="flex gap-2">
        {onView && (
          <button
            onClick={() => onView(recruiter)}
            className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            View
          </button>
        )}
        {!recruiter.approved && onApprove && (
          <button
            onClick={() => onApprove(recruiter)}
            className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
          >
            Approve
          </button>
        )}
        {!recruiter.approved && onReject && (
          <button
            onClick={() => onReject(recruiter)}
            className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
          >
            Reject
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Mobile Card Component for Recruiter Tokens
 */
export const MobileRecruiterTokenCard = ({ recruiter, activeTokens, isSelected, onToggleSelect, onGenerateToken, generatingToken, onToggleTokens, expandedRecruiter, onRevokeToken, revokingToken }) => {
  const recruiterTokens = Array.isArray(activeTokens) 
    ? activeTokens.filter(token => token.recruiter_id === recruiter.id)
    : [];
  const hasTokens = recruiterTokens.length > 0;
  const isExpanded = expandedRecruiter === recruiter.id;
  
  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          {!recruiter.password_hash && (recruiter.approved || recruiter.status === 'approved') ? (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelect}
              className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
            />
          ) : (
            <div className="w-4 h-4 mt-1"></div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
              {recruiter.name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 break-all">
              {recruiter.email}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {recruiter.organization}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            recruiter.approved || recruiter.status === 'approved'
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : recruiter.status === 'rejected'
              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
          }`}>
            {recruiter.status || (recruiter.approved ? 'approved' : 'pending')}
          </span>
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            recruiter.password_hash
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
          }`}>
            {recruiter.password_hash ? 'Account Set Up' : 'Setup Pending'}
          </span>
        </div>
      </div>
      
      <div className="flex flex-wrap gap-2 mt-4">
        {!recruiter.password_hash && (recruiter.approved || recruiter.status === 'approved') && (
          <button
            onClick={() => onGenerateToken(recruiter.id)}
            disabled={generatingToken === recruiter.id}
            className={`flex-1 px-3 py-2 text-xs font-medium rounded-md text-white ${
              generatingToken === recruiter.id
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {generatingToken === recruiter.id ? 'Generating...' : 'Generate Token'}
          </button>
        )}
        
        {recruiter.password_hash && (
          <span className="text-green-600 text-xs py-2">✓ Complete</span>
        )}
        
        {hasTokens && (
          <button
            onClick={() => onToggleTokens(isExpanded ? null : recruiter.id)}
            className="px-3 py-2 text-xs text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md"
          >
            {isExpanded ? 'Hide' : 'Show'} {recruiterTokens.length} Token{recruiterTokens.length > 1 ? 's' : ''}
          </button>
        )}
      </div>

      {/* Expanded tokens */}
      {hasTokens && isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h4 className="font-medium text-gray-900 dark:text-white mb-3">Active Tokens</h4>
          <div className="space-y-3">
            {recruiterTokens.map((token) => (
              <div key={token.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <div className="text-xs font-mono text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 p-2 rounded mb-2 break-all">
                  {token.token}
                </div>
                <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
                  <div>
                    <div>Created: {new Date(token.created_at).toLocaleDateString()}</div>
                    <div>Expires: {new Date(token.expires_at).toLocaleDateString()}</div>
                  </div>
                  <button
                    onClick={() => onRevokeToken(token.id)}
                    disabled={revokingToken === token.id}
                    className={`px-3 py-1 text-xs rounded ${
                      revokingToken === token.id
                        ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                  >
                    {revokingToken === token.id ? 'Revoking...' : 'Revoke'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Mobile Card Component for Pending Students (Approve Students Tab)
 */
export const MobilePendingStudentCard = ({ student, isSelected, onToggleSelect }) => {
  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
          />
          <div className="flex-1">
            <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
              {student.first_name} {student.last_name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 break-all">
              {student.university_email}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              ID: {student.id}
            </p>
          </div>
        </div>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          student.status === 'pending'
            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
            : student.status === 'approved' 
            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
        }`}>
          {student.status}
        </span>
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="font-medium text-gray-700 dark:text-gray-300">University:</span>
          <span className="text-gray-600 dark:text-gray-400 text-right">{student.university}</span>
        </div>
        {student.batch_id && (
          <div className="flex justify-between">
            <span className="font-medium text-gray-700 dark:text-gray-300">Batch:</span>
            <span className="text-gray-600 dark:text-gray-400">{student.batch_id}</span>
          </div>
        )}
        {student.major && (
          <div className="flex justify-between">
            <span className="font-medium text-gray-700 dark:text-gray-300">Major:</span>
            <span className="text-gray-600 dark:text-gray-400">{student.major}</span>
          </div>
        )}
        {student.current_academic_class && (
          <div className="flex justify-between">
            <span className="font-medium text-gray-700 dark:text-gray-300">Class:</span>
            <span className="text-gray-600 dark:text-gray-400">{student.current_academic_class}</span>
          </div>
        )}
        {student.gpa && (
          <div className="flex justify-between">
            <span className="font-medium text-gray-700 dark:text-gray-300">GPA:</span>
            <span className="text-gray-600 dark:text-gray-400">{student.gpa}</span>
          </div>
        )}
        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between">
            <span className="font-medium text-gray-700 dark:text-gray-300">Uploaded by:</span>
            <span className="text-gray-600 dark:text-gray-400">{student.uploaded_by}</span>
          </div>
          {student.uploaded_at && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700 dark:text-gray-300">Date:</span>
              <span className="text-gray-600 dark:text-gray-400">
                {new Date(student.uploaded_at).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Mobile Card Component for University Partner Activity
 */
export const MobilePartnerActivityCard = ({ partner }) => {
  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white break-words">
            {partner.university}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 break-words">
            {partner.partner_name}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500 break-all mt-1">
            {partner.email}
          </p>
        </div>
        <div className="flex flex-col gap-1 items-end">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            partner.is_active
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
          }`}>
            {partner.is_active ? 'Active' : 'Inactive'}
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            {partner.total_logins || 0} logins
          </span>
        </div>
      </div>
      
      <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center">
          <span className="font-medium text-gray-700 dark:text-gray-300 text-sm">Last Login:</span>
          <div className="text-right">
            {partner.last_login ? (
              <div className="text-sm text-gray-600 dark:text-gray-300">
                <div className="font-medium">{new Date(partner.last_login).toLocaleDateString()}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {new Date(partner.last_login).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ) : (
              <span className="text-gray-400 dark:text-gray-500 italic text-sm">Never</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Mobile Card Component for Student Matches
 */
export const MobileStudentMatchCard = ({ student }) => {
  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white break-words">
            {student.name}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 break-all">
            {student.email}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
            ID: {student.id}
          </p>
        </div>
        <div className="flex flex-col gap-1 items-end">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            GPA: {student.gpa}
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            {student.class_year}
          </span>
        </div>
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="font-medium text-gray-700 dark:text-gray-300">University:</span>
          <span className="text-gray-600 dark:text-gray-400 text-right break-words">{student.university}</span>
        </div>
        <div className="flex justify-between">
          <span className="font-medium text-gray-700 dark:text-gray-300">Major:</span>
          <span className="text-gray-600 dark:text-gray-400 text-right break-words">{student.major}</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Mobile Card Component for Multi-Company Students
 */
export const MobileMultiCompanyStudentCard = ({ studentData, formatCompanies }) => {
  const { student_info, matching_organizations, match_count } = studentData;
  
  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white break-words">
            {student_info.name}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 break-all">
            {student_info.email}
          </p>
        </div>
        <div className="flex flex-col gap-1 items-end">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            {match_count} matches
          </span>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            student_info.verification_status === 'verified'
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
          }`}>
            {student_info.verification_status === 'verified' ? 'Verified' : 'Pending'}
          </span>
        </div>
      </div>
      
      <div className="space-y-3">
        <div>
          <span className="font-medium text-gray-700 dark:text-gray-300 text-sm">Companies:</span>
          <div className="mt-1 text-sm text-gray-600 dark:text-gray-400 break-words">
            {formatCompanies(matching_organizations)}
          </div>
        </div>
        
        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-700 dark:text-gray-300 text-sm">Eligibility:</span>
            <div>
              {student_info.eligibility_status !== null ? (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  student_info.eligibility_status === true
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                }`}>
                  {student_info.eligibility_status === true ? 'Eligible' : 'Not Eligible'}
                </span>
              ) : (
                <span className="text-gray-400 dark:text-gray-500 italic text-sm">-</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Mobile Card Component for University Missing Students
 */
export const MobileUniversityMissingStudentCard = ({ student }) => {
  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
            {student.student_name}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            ID: {student.applicant_id}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            student.verification_status === 'verified'
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
          }`}>
            {student.verification_status}
          </span>
          {student.eligibility !== null && (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              student.eligibility === true
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            }`}>
              {student.eligibility === true ? 'Eligible' : 'Not Eligible'}
            </span>
          )}
        </div>
      </div>
      
      <div className="space-y-2 text-sm mb-4">
        {student.university_email && (
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">Email:</span>
            <p className="text-gray-600 dark:text-gray-400 break-all">{student.university_email}</p>
          </div>
        )}
        {student.scholarship_verified && (
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">Scholarship:</span>
            <p className="text-gray-600 dark:text-gray-400">{student.scholarship_verified}</p>
          </div>
        )}
        {student.last_application_formatted && (
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">Last Application:</span>
            <p className="text-gray-600 dark:text-gray-400">{student.last_application_formatted}</p>
          </div>
        )}
        {student.verified_at_formatted && (
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">Date Verified:</span>
            <p className="text-gray-600 dark:text-gray-400">
              {student.verified_at_formatted}
              {student.days_since_verification !== undefined && (
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                  ({student.days_since_verification} days ago)
                </span>
              )}
            </p>
          </div>
        )}
        {student.preferred_companies && (
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">Preferred Companies:</span>
            <p className="text-gray-600 dark:text-gray-400">{student.preferred_companies}</p>
          </div>
        )}
      </div>
      
      <div className="flex gap-2 flex-wrap">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          Total Apps: {student.total_applications || 0}
        </span>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
          Cycle: {student.current_cycle_year}
        </span>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
          This Cycle: {student.applications_this_cycle || 0}
        </span>
      </div>
    </div>
  );
};

/**
 * Mobile Card Component for Students
 */
export const MobileStudentCard = ({ student, onView, onAction }) => {
  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
            {student.student_name || `${student.first_name} ${student.last_name}`}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            ID: {student.id || student.applicant_id}
          </p>
        </div>
        {student.verification_status && (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            student.verification_status === 'verified'
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
          }`}>
            {student.verification_status}
          </span>
        )}
      </div>
      
      <div className="space-y-2 text-sm mb-4">
        {student.university_email && (
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">Email:</span>
            <p className="text-gray-600 dark:text-gray-400 break-all">{student.university_email}</p>
          </div>
        )}
        {student.current_university_verified && (
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">University:</span>
            <p className="text-gray-600 dark:text-gray-400">{student.current_university_verified}</p>
          </div>
        )}
        {student.major_verified && (
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">Major:</span>
            <p className="text-gray-600 dark:text-gray-400">{student.major_verified}</p>
          </div>
        )}
        {student.gpa_verified && (
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">GPA:</span>
            <p className="text-gray-600 dark:text-gray-400">{student.gpa_verified}</p>
          </div>
        )}
      </div>
      
      {(onView || onAction) && (
        <div className="flex gap-2">
          {onView && (
            <button
              onClick={() => onView(student)}
              className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              View Details
            </button>
          )}
          {onAction && (
            <button
              onClick={() => onAction(student)}
              className="flex-1 px-3 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
            >
              Action
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Device Detection Hook
 */
export const useDeviceDetect = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [screenSize, setScreenSize] = useState('lg');

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
      
      if (width < 640) setScreenSize('xs');
      else if (width < 768) setScreenSize('sm');
      else if (width < 1024) setScreenSize('md');
      else if (width < 1280) setScreenSize('lg');
      else setScreenSize('xl');
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  return { isMobile, isTablet, screenSize };
};

/**
 * Mobile Card Component for Admin Student Matches
 * Displays student with their matching organizations in a mobile-friendly format
 */
export const MobileAdminStudentMatchCard = ({ 
  studentData, 
  isSelected, 
  onToggleSelect, 
  onGenerateToken, 
  tokenGenerating, 
  isExpanded, 
  onToggleExpand,
  expandedOrgItems,
  setExpandedOrgItems
}) => {
  const { student_info, matching_organizations, match_count } = studentData;

  // Component for collapsible requirement dropdowns in mobile org cards
  const MobileOrgRequirementDropdown = ({ label, items, orgIndex, fieldName }) => {
    const dropdownKey = `org-${orgIndex}-${fieldName}`;
    const isDropdownExpanded = expandedOrgItems.has(dropdownKey);
    
    const toggleDropdown = (e) => {
      e.stopPropagation();
      const newExpanded = new Set(expandedOrgItems);
      if (newExpanded.has(dropdownKey)) {
        newExpanded.delete(dropdownKey);
      } else {
        newExpanded.add(dropdownKey);
      }
      setExpandedOrgItems(newExpanded);
    };

    if (!items || items.length === 0) {
      return (
        <div>
          <p className="font-medium text-gray-700 dark:text-gray-300 text-xs">{label}:</p>
          <p className="text-gray-600 dark:text-gray-400 text-xs">Not specified</p>
        </div>
      );
    }

    const displayText = items.length === 1 ? items[0] : `${items.length} items`;
    const shouldShowDropdown = items.length > 1;

    return (
      <div>
        <p className="font-medium text-gray-700 dark:text-gray-300 text-xs">{label}:</p>
        {shouldShowDropdown ? (
          <div>
            <button
              onClick={toggleDropdown}
              className="flex items-center space-x-1 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors text-xs"
            >
              <span>{displayText}</span>
              <svg 
                className={`w-3 h-3 transform transition-transform ${isDropdownExpanded ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isDropdownExpanded && (
              <div className="mt-1 p-2 bg-gray-50 dark:bg-gray-600 rounded border text-xs">
                <ul className="list-disc list-inside space-y-1">
                  {items.map((item, idx) => (
                    <li key={idx} className="text-gray-700 dark:text-gray-300 text-xs">{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-600 dark:text-gray-400 text-xs">{displayText}</p>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Student Header */}
      <div className="p-4">
        <div className="flex items-start gap-3 mb-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(student_info.id)}
            className="mt-1 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
          />
          <div className="flex-1 min-w-0">
            <div className="flex flex-col gap-2 mb-2">
              <h3 className="font-bold text-lg text-gray-900 dark:text-white break-words">
                {student_info.name}
              </h3>
              <div className="flex flex-wrap gap-1">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  match_count > 0 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {match_count} {match_count === 1 ? 'Match' : 'Matches'}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onGenerateToken(student_info.id);
                  }}
                  disabled={tokenGenerating}
                  className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m0 0a2 2 0 012 2m-2-2v6m0 0a2 2 0 01-2 2m2-2a2 2 0 002-2m-4 4H9.5a2.5 2.5 0 010-5H11m0 0a2 2 0 012-2m-2 2v4a2 2 0 01-2 2m2-2a2 2 0 002-2z" />
                  </svg>
                  Token
                </button>
              </div>
            </div>
            
            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300 mb-3">
              <p><strong>Email:</strong> <span className="break-all">{student_info.email}</span></p>
              <p><strong>University:</strong> <span className="break-words">{student_info.university}</span></p>
              <div className="flex flex-wrap gap-4">
                <p><strong>GPA:</strong> <span className="text-green-600 dark:text-green-400 font-medium">{student_info.gpa}</span></p>
                <p><strong>Major:</strong> <span className="break-words">{student_info.major}</span></p>
                <p><strong>Class Year:</strong> {student_info.class_year}</p>
              </div>
            </div>

            {/* Expand/Collapse Button */}
            <button
              onClick={onToggleExpand}
              className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 text-sm"
            >
              <span>{isExpanded ? 'Hide' : 'Show'} Organizations ({match_count})</span>
              <svg 
                className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Expanded Organizations */}
        {isExpanded && match_count > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm">
              Matching Organizations
            </h4>
            <div className="space-y-3">
              {matching_organizations.map((org, orgIndex) => (
                <div key={orgIndex} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                  <div className="flex flex-col gap-2 mb-2">
                    <div className="flex items-center justify-between gap-2">
                      <h5 className="font-semibold text-gray-900 dark:text-white text-sm break-words">
                        {org.organization}
                      </h5>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        org.approved
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}>
                        {org.approved ? 'APPROVED' : 'PENDING'}
                      </span>
                    </div>
                    
                    <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
                      <p><strong>Recruiter:</strong> {org.recruiter_name}</p>
                      <p><strong>Email:</strong> <span className="break-all">{org.recruiter_email}</span></p>
                      <p><strong>Min GPA:</strong> {org.minimum_gpa}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 border-t border-gray-200 dark:border-gray-600 pt-2">
                    <MobileOrgRequirementDropdown 
                      label="Eligible Majors"
                      items={org.eligible_majors}
                      orgIndex={`mobile-${orgIndex}`}
                      fieldName="majors"
                    />
                    <MobileOrgRequirementDropdown 
                      label="Preferred Institutions"
                      items={org.preferred_institutions}
                      orgIndex={`mobile-${orgIndex}`}
                      fieldName="institutions"
                    />
                    <div>
                      <p className="font-medium text-gray-700 dark:text-gray-300 text-xs">Preferred Class Years:</p>
                      <p className="text-gray-600 dark:text-gray-400 text-xs">
                        {org.preferred_class_years ? org.preferred_class_years.join(', ') : 'Not specified'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Mobile Card Component for Non-matching Students
 * Displays student info for students without organization matches
 */
export const MobileNonMatchingStudentCard = ({ 
  studentData, 
  isSelected, 
  onToggleSelect, 
  onGenerateToken, 
  tokenGenerating 
}) => {
  const { student_info } = studentData;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(student_info.id)}
            className="mt-1 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
          />
          <div className="flex-1 min-w-0">
            <div className="flex flex-col gap-2 mb-3">
              <h3 className="font-bold text-lg text-gray-900 dark:text-white break-words">
                {student_info.name}
              </h3>
              <div className="flex flex-wrap gap-1">
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  {student_info.scholarship_type}
                </span>
                <button
                  onClick={() => onGenerateToken(student_info.id)}
                  disabled={tokenGenerating}
                  className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m0 0a2 2 0 012 2m-2-2v6m0 0a2 2 0 01-2 2m2-2a2 2 0 002-2m-4 4H9.5a2.5 2.5 0 010-5H11m0 0a2 2 0 012-2m-2 2v4a2 2 0 01-2 2m2-2a2 2 0 002-2z" />
                  </svg>
                  Token
                </button>
              </div>
            </div>
            
            <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
              <p><strong>Email:</strong> <span className="break-all">{student_info.email}</span></p>
              <p><strong>University:</strong> <span className="break-words">{student_info.university}</span></p>
              <p><strong>Major:</strong> <span className="break-words">{student_info.major}</span></p>
              <div className="flex flex-wrap gap-4">
                <p><strong>GPA:</strong> <span className="text-green-600 dark:text-green-400 font-medium">{student_info.gpa}</span></p>
                <p><strong>Class Year:</strong> {student_info.class_year}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Mobile Card Component for Company Management
 * Displays company info with activate/deactivate functionality
 */
export const MobileCompanyCard = ({ 
  company, 
  onToggleStatus 
}) => {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden ${
      !company.is_active ? 'bg-red-50 dark:bg-red-900/20' : ''
    }`}>
      <div className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg text-gray-900 dark:text-white break-words">
              {company.organization || 'No Organization'}
            </h3>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                company.is_active 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              }`}>
                {company.is_active ? 'Active' : 'Inactive'}
              </span>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                company.approved 
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
              }`}>
                {company.approved ? 'Approved' : 'Pending'}
              </span>
            </div>
          </div>
        </div>
        
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300 mb-4">
          <p><strong>Recruiter:</strong> <span className="break-words">{company.name}</span></p>
          <p><strong>Email:</strong> <span className="break-all">{company.email}</span></p>
        </div>

        <div className="flex justify-end">
          <button
            onClick={() => onToggleStatus(company.id, company.organization, company.is_active)}
            className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              company.is_active
                ? 'text-red-700 bg-red-100 hover:bg-red-200 focus:ring-red-500 dark:text-red-200 dark:bg-red-900 dark:hover:bg-red-800'
                : 'text-green-700 bg-green-100 hover:bg-green-200 focus:ring-green-500 dark:text-green-200 dark:bg-green-900 dark:hover:bg-green-800'
            }`}
          >
            {company.is_active ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * MobileAdminUserCard Component
 * Mobile-friendly card for displaying admin user information
 */
export const MobileAdminUserCard = ({ user, onEdit, onDelete }) => {
  return (
    <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm">
      <div className="space-y-3">
        {/* User Info Header */}
        <div className="flex flex-col gap-2">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {user.full_name}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 break-all">
                {user.email}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                @{user.username}
              </p>
            </div>
          </div>
          
          {/* Status Badges */}
          <div className="flex flex-wrap gap-2">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              user.is_active 
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            }`}>
              {user.is_active ? 'Active' : 'Inactive'}
            </span>
            {user.is_super_admin && (
              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                Super Admin
              </span>
            )}
          </div>
        </div>

        {/* Created Date */}
        <div className="text-sm text-gray-600 dark:text-gray-300">
          <strong>Created:</strong> {new Date(user.created_at).toLocaleDateString()}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-600">
          <button
            onClick={() => onEdit(user)}
            className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            Edit
          </button>
          <button
            onClick={() => onDelete(user.id)}
            className="flex-1 px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Responsive Container Component
 * Adds proper padding for mobile navigation
 */
export const ResponsiveContainer = ({ children, hasMobileNav = false, hasMobileSidebar = false }) => {
  return (
    <div className={`min-h-screen ${
      hasMobileNav ? 'pb-20 md:pb-0' : ''
    } ${
      hasMobileSidebar ? 'pt-14 md:pt-0' : ''
    }`}>
      {children}
    </div>
  );
};